import { Inject, Component, OnInit, OnDestroy, ChangeDetectionStrategy, ChangeDetectorRef } from '@angular/core';
import { AngularFireAuth } from '@angular/fire/compat/auth';
import { ActivatedRoute } from '@angular/router';
import { AuthService } from 'src/app/shared/services/auth';
import { BoardUserData } from '../up-board-editor/up-board-editor.component';
import { DOCUMENT } from '@angular/common';
import * as icons from '@fortawesome/free-solid-svg-icons';

import { AngularFirestore } from '@angular/fire/compat/firestore';
import { Board, Delivery, Employee } from '../up-board/up-board.component';
import { TimeUtils } from 'src/app/utils/time-utils';
import { 
  getUsersNotInState,
  normalizeEmployee,
  normalizeEmployees,
  isEmployeeUnavailable,
  isEmployeeInactive
} from 'src/app/utils/board-user-utils';
import {
  NormalizedEmployee,
  ChannelType,
} from 'src/app/utils/employee.model';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

declare var $: any;

@Component({
  selector: 'app-up-board-viewer',
  templateUrl: './up-board-viewer.component.html',
  styleUrls: ['./up-board-viewer.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
/**
 * UpBoardViewerComponent - Read-Only Multi-Channel Board Viewer
 * 
 * This component is a read-only viewer version of UpBoardComponent that displays
 * a 2-4 column layout for viewing employee queues across multiple channels (instore,
 * phone, online). It mirrors the editing board's layout but prevents all modifications.
 * 
 * **Core Features:**
 * - Multi-column dynamic layout identical to UpBoardComponent
 * - Real-time employee list updates with <500ms latency (read-only, no writing)
 * - Per-channel employee filtering and ordering (Floor Ups, With Guest, Phone Ups, Internet Ups)
 * - NO drag-and-drop (read-only viewer)
 * - Fullscreen support for presentation on external displays
 * - Clock display showing current time (updates every second)
 * 
 * **Data Architecture:**
 * - Normalized multi-channel data model (NormalizedEmployee with channels[instore/phone/online])
 * - Automatic conversion from legacy format to normalized for display
 * - Per-channel state and order values for independent queue visualization
 * - Cross-channel busy detection for visual indication in phone/online columns
 * 
 * **Performance Optimizations:**
 * - ChangeDetectionStrategy.OnPush for minimal change detection cycles
 * - Manual markForCheck() calls after state updates
 * - RxJS takeUntil() pattern for subscription cleanup (no memory leaks)
 * - Efficient filtering with sort operations O(n log n) per update
 * 
 * **Column Definitions (Identical to UpBoardComponent):**
 * - **Floor Ups**: Available, with_apt, with_apt_delivery, break, inactive (instore only)
 * - **With Guest**: ONLY with_guest, with_guest_delivery (instore only, always visible)
 * - **Phone Ups**: Employees eligible for phone channel (visible when enablePhoneChannel=true)
 * - **Internet Ups**: Employees eligible for online channel (visible when enableOnlineChannel=true)
 * 
 * **Integration Points:**
 * - AngularFirestore subscriptions for real-time board config and user data (read-only)
 * - No AuthTokenService (no administrative actions)
 * - UpBoardEmployeeComponent for individual employee card display
 * - No TransactionBoardService (read-only - no state transitions)
 * 
 * **UI Features:**
 * - Fullscreen toggle button (top-right corner)
 * - Display clock with current time (updates every second)
 * - Mouse move detection hides controls after 1 second of inactivity
 * - Cross-browser fullscreen support (webkit, moz, ms prefixes)
 * 
 * @see Story 3.1 - Implement Multi-Column Board Layout (2-4 Columns)
 * @see UpBoardComponent - Editing board version with drag-drop support
 */
export class UpBoardViewerComponent implements OnInit, OnDestroy {
  icons = icons;
  boardId: string | null = null;
  boardUser: BoardUserData | undefined;
  loaded: boolean = false;
  board: Board | null | undefined;
  users: BoardUserData[] = [];
  active: Employee[] = [];
  up: Employee | undefined;
  onDeck: Employee | undefined;
  
  // Per-channel UP/On Deck tracking
  instoreUp: NormalizedEmployee | undefined;
  instoreOnDeck: NormalizedEmployee | undefined;
  phoneUp: NormalizedEmployee | undefined;
  phoneOnDeck: NormalizedEmployee | undefined;
  onlineUp: NormalizedEmployee | undefined;
  onlineOnDeck: NormalizedEmployee | undefined;
  deliveries: Delivery[] = [];

  /** Normalized employees for multi-channel support */
  normalizedUsers: NormalizedEmployee[] = [];

  /** Whether to show delivery features for this board (defaults to true) */
  showDelivery: boolean = true;

  now: Date = new Date();
  timeUtils: TimeUtils = new TimeUtils();
  timeout: NodeJS.Timeout | undefined;

  // =============================================
  // Multi-Channel Support (Story 3.1)
  // =============================================
  
  /** Board configuration with feature flags for channels */
  boardConfig: any = {};
  
  /** Array of enabled channels based on board config */
  enabledChannels: ChannelType[] = ['instore'];

  // Per-channel employee lists (computed from users array)
  floorUpsEmployees: NormalizedEmployee[] = [];           // Floor Ups column (instore)
  withGuestEmployees: NormalizedEmployee[] = [];          // With Guest column (instore)
  phoneUpsEmployees: NormalizedEmployee[] = [];           // Phone Ups column
  internetUpsEmployees: NormalizedEmployee[] = [];        // Internet Ups column

  // =============================================
  // Legacy Mode Support (Story 3.9)
  // =============================================

  /**
   * Determines if the board should display in legacy viewer mode.
   * 
   * Rules:
   * - If instore channel is disabled (enableInstoreChannel=false) → return false (can't use legacy without instore)
   * - Multi-channel boards (phone OR online enabled) MUST use modern UI → return false
   * - Single-channel boards with useModernLayout=true → return false (opt-in to modern)
   * - Single-channel boards with useModernLayout=false/undefined → return true (legacy default)
   */
  get isLegacyMode(): boolean {
    // If instore channel is disabled, MUST use modern UI (can't use legacy without instore)
    if (this.boardConfig?.enableInstoreChannel === false) {
      console.log('[DEBUG isLegacyMode VIEWER] Instore disabled, returning false');
      return false;
    }
    
    // A channel is only "active" if it's both purchased AND enabled
    // This prevents stale enablePhoneChannel/enableOnlineChannel values from forcing modern UI
    const phoneActive = this.boardConfig?.phoneChannelPurchased === true && 
                        this.boardConfig?.enablePhoneChannel === true;
    const onlineActive = this.boardConfig?.internetChannelPurchased === true && 
                         this.boardConfig?.enableOnlineChannel === true;
    
    // Multi-channel boards MUST use modern UI
    if (phoneActive || onlineActive) {
      console.log('[DEBUG isLegacyMode VIEWER] Multi-channel enabled, returning false. phoneActive=', phoneActive, 'onlineActive=', onlineActive);
      return false;
    }
    // Single-channel boards: use legacy unless explicitly opted into modern UI
    const result = this.boardConfig?.useModernLayout !== true;
    console.log('[DEBUG isLegacyMode VIEWER] Single-channel: useModernLayout=', this.boardConfig?.useModernLayout, 'type=', typeof this.boardConfig?.useModernLayout, '→ isLegacyMode=', result);
    return result;
  }

  /**
   * Gets the legacy state for an employee, handling both old and new data formats.
   * Maps normalized 'available' back to 'up' for legacy UI compatibility.
   */
  getLegacyState(emp: NormalizedEmployee): string {
    if (emp.channels?.instore?.state) {
      const state = emp.channels.instore.state;
      // Map 'available' back to 'up' for legacy UI compatibility
      return state === 'available' ? 'up' : state;
    }
    return (emp as any).state || 'inactive';
  }

  /**
   * Legacy helper: filter employees by single state.
   * Returns objects with emp and index for template compatibility.
   */
  legacyInState(state: string): {emp: NormalizedEmployee, index: number}[] {
    return this.normalizedUsers
      .filter(emp => this.getLegacyState(emp) === state)
      .sort((a, b) => (a.channels?.instore?.order || 0) - (b.channels?.instore?.order || 0))
      .map((emp, index) => ({ emp, index }));
  }

  /**
   * Legacy helper: filter employees by multiple states.
   * Returns objects with emp and index for template compatibility.
   */
  legacyInStates(states: string[]): {emp: NormalizedEmployee, index: number}[] {
    return this.normalizedUsers
      .filter(emp => states.includes(this.getLegacyState(emp)))
      .sort((a, b) => (a.channels?.instore?.order || 0) - (b.channels?.instore?.order || 0))
      .map((emp, index) => ({ emp, index }));
  }

  /**
   * Gets the "UP" employee for legacy layout (first available in queue).
   */
  get legacyUp(): NormalizedEmployee | undefined {
    const available = this.normalizedUsers
      .filter(emp => this.getLegacyState(emp) === 'up')
      .sort((a, b) => (a.channels?.instore?.order || 0) - (b.channels?.instore?.order || 0));
    return available[0];
  }

  /**
   * Gets the "On Deck" employee for legacy layout (second available in queue).
   */
  get legacyOnDeck(): NormalizedEmployee | undefined {
    const available = this.normalizedUsers
      .filter(emp => this.getLegacyState(emp) === 'up')
      .sort((a, b) => (a.channels?.instore?.order || 0) - (b.channels?.instore?.order || 0));
    return available[1];
  }

  /** RxJS cleanup subscription */
  private destroy$ = new Subject<void>();

  constructor(
    public db2: AngularFirestore,
    public auth: AngularFireAuth,
    private route: ActivatedRoute,
    public authService: AuthService,
    @Inject(DOCUMENT) private document: any,
    private cdr: ChangeDetectorRef
  ) {
    this.updateNow();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  toggleVisible = false;

  onMouseMove() {
    if (this.timeout) {
      clearTimeout(this.timeout);
    }
    $('#btnToggle').css('opacity', '100%');

    this.timeout = setTimeout(() => {
      $('#btnToggle').animate(
        { opacity: '0%' },
        {
          duration: 100,
        }
      );
    }, 1000);
  }

  toggleFullscreen() {
    // if already full screen; exit
    // else go fullscreen
    if (
      this.document.fullscreenElement ||
      this.document.webkitFullscreenElement ||
      this.document.mozFullScreenElement ||
      this.document.msFullscreenElement
    ) {
      if (this.document.exitFullscreen) {
        this.document.exitFullscreen();
      } else if (this.document.mozCancelFullScreen) {
        this.document.mozCancelFullScreen();
      } else if (this.document.webkitExitFullscreen) {
        this.document.webkitExitFullscreen();
      } else if (this.document.msExitFullscreen) {
        this.document.msExitFullscreen();
      }
    } else {
      var element = $('#view-container').get(0);
      if (element.requestFullscreen) {
        element.requestFullscreen();
      } else if (element.mozRequestFullScreen) {
        element.mozRequestFullScreen();
      } else if (element.webkitRequestFullscreen) {
        element.webkitRequestFullscreen();
      } else if (element.msRequestFullscreen) {
        element.msRequestFullscreen();
      }
    }
  }

  /**
   * Update the current time every second
   */
  updateNow() {
    this.now = new Date();
    setTimeout(() => {
      this.updateNow();
    }, 1000);
  }

  available(employees: Employee[] | undefined): EmployeeWithIndex[] {
    return this.inState(employees, 'up');
  }

  withGuest(employees: Employee[] | undefined): EmployeeWithIndex[] {
    return this.inState(employees, 'with_guest');
  }

  withApt(employees: Employee[] | undefined): EmployeeWithIndex[] {
    return this.inState(employees, 'with_apt');
  }

  delivery(employees: Employee[] | undefined): EmployeeWithIndex[] {
    return this.inStates(employees, [
      'delivery',
      'with_guest_delivery',
      'with_apt_delivery',
    ]);
  }

  break(employees: Employee[] | undefined): EmployeeWithIndex[] {
    return this.inState(employees, 'break');
  }

  inState(
    employees: Employee[] | undefined,
    state: string
  ): EmployeeWithIndex[] {
    if (!employees) return [];

    const in_state: EmployeeWithIndex[] = [];

    var idx = 0;
    for (var i = 0; i < employees.length; i++) {
      var emp = employees[i];
      if (emp.state !== 'with_guest') {
        idx++;
      }
      if (emp.state === state) {
        in_state.push({
          emp: emp,
          index: idx,
        });
      }
    }

    return in_state;
  }

  inStates(
    employees: Employee[] | undefined,
    states: string[]
  ): EmployeeWithIndex[] {
    if (!employees) return [];

    const emps = this.unique(employees);

    const in_states: EmployeeWithIndex[] = [];

    var idx = 0;
    for (var i = 0; i < emps.length; i++) {
      var emp = emps[i];
      if (emp.state !== 'with_guest' && emp.state != 'with_guest_delivery') {
        idx++;
      }
      for (const state of states) {
        if (emp.state === state) {
          in_states.push({
            emp: emp,
            index: idx,
          });
        }
      }
    }

    return in_states;
  }

  /**
   * Filter deliveries by in-store status and sort by arrival time
   */
  inStore(deliveries: Delivery[] | undefined, arrived: boolean): Delivery[] {
    if (!deliveries) return [];

    const val: Delivery[] = [];

    for (const delivery of deliveries) {
      if (delivery.customer_in_store === arrived) {
        val.push(delivery);
      }
    }

    val.sort((a: Delivery, b: Delivery) => {
      if (a.customer_in_store != b.customer_in_store) {
        if (a.customer_in_store) return -1;
        return 1;
      }
      
      const aTime = a.arrival_time instanceof Date ? a.arrival_time.getTime() : new Date(a.arrival_time).getTime();
      const bTime = b.arrival_time instanceof Date ? b.arrival_time.getTime() : new Date(b.arrival_time).getTime();
      
      if (aTime > bTime) return 1;
      if (aTime < bTime) return -1;
      return 0;
    });

    return val;
  }

  onFullScreenChange(event: any) {
    if (document.fullscreenElement) {
      console.log('enter fullscreen');
      $('#btnToggle').css('visibility', 'hidden');
    } else {
      console.log('exit fullscreen');
      $('#btnToggle').css('visibility', 'visible');
    }
  }

  unique(emps: Employee[] | undefined) {
    const uniqueArray: Employee[] = [];

    emps?.forEach((a) => {
      const index = uniqueArray.findIndex((b) => {
        return b.email === a.email;
      });

      if (index === -1) {
        uniqueArray.push(a);
      }
    });

    return uniqueArray;
  }

  /**
   * Update enabled channels based on board configuration.
   * Channels are only enabled if BOTH purchased (by super-admin) AND enabled (by admin).
   * 
   * Note: enableInstoreChannel and instoreChannelPurchased default to true for backward compatibility
   * (Walk-ins is the original/core functionality).
   * 
   * @param boardConfig Board configuration with purchased and enable flags for each channel
   */
  updateEnabledChannels(boardConfig: any): void {
    this.enabledChannels = [];
    
    // Instore: purchased defaults to true (backward compat), enabled defaults to true
    const instorePurchased = boardConfig?.instoreChannelPurchased !== false;
    const instoreEnabled = boardConfig?.enableInstoreChannel !== false;
    if (instorePurchased && instoreEnabled) {
      this.enabledChannels.push('instore');
    }
    
    // Phone: must be BOTH purchased AND enabled
    const phonePurchased = boardConfig?.phoneChannelPurchased === true;
    const phoneEnabled = boardConfig?.enablePhoneChannel === true;
    if (phonePurchased && phoneEnabled) {
      this.enabledChannels.push('phone');
    }
    
    // Online: must be BOTH purchased AND enabled
    const onlinePurchased = boardConfig?.internetChannelPurchased === true;
    const onlineEnabled = boardConfig?.enableOnlineChannel === true;
    if (onlinePurchased && onlineEnabled) {
      this.enabledChannels.push('online');
    }
  }

  /**
   * Calculate per-channel UP/On Deck employees based on current state and config.
   * This must be called whenever:
   * 1. The employee list changes
   * 2. The board configuration changes (affects unavailability calculations)
   * 
   * This calculation determines which employees are shown as green (UP) or yellow (On Deck)
   * in each channel's queue.
   */
  calculateUpAndOnDeck(): void {
    // Instore: first two available employees in floor ups (not on break, not busy, not inactive)
    const availableInstore = this.floorUpsEmployees.filter(emp => {
      if (emp.globalState === 'break') return false;
      const state = emp.channels?.instore?.state;
      // 'available' is the normalized state for employees ready to take customers
      // (legacy 'up'/'onDeck' states are normalized to 'available')
      return state === 'available';
    });
    this.instoreUp = availableInstore[0];
    this.instoreOnDeck = availableInstore[1];
    
    // Phone: first two available (non-unavailable) employees in the phone queue
    const availablePhone = this.phoneUpsEmployees.filter(emp => !this.isEmployeeUnavailable(emp, 'phone'));
    this.phoneUp = availablePhone[0];
    this.phoneOnDeck = availablePhone[1];
    
    // Online: first two available (non-unavailable) employees in the internet queue
    const availableOnline = this.internetUpsEmployees.filter(emp => !this.isEmployeeUnavailable(emp, 'online'));
    this.onlineUp = availableOnline[0];
    this.onlineOnDeck = availableOnline[1];
  }

  /**
   * Filter and organize normalized employees into per-channel lists.
   * Called whenever the normalized employees array changes.
   */
  updateEmployeeLists(): void {
    // Floor Ups: All instore employees EXCEPT with_guest, with_guest_delivery, AND inactive
    // Story 5.3: Use globalState === 'inactive' via isEmployeeInactive() helper
    this.floorUpsEmployees = this.normalizedUsers.filter(emp => {
      if (!emp.channels?.instore) return false;
      const state = emp.channels.instore.state;
      return state !== 'with_guest' && state !== 'with_guest_delivery' && !isEmployeeInactive(emp);
    }).sort((a, b) => (a.channels?.instore?.order ?? 0) - (b.channels?.instore?.order ?? 0));

    // With Guest: ONLY with_guest or with_guest_delivery states
    this.withGuestEmployees = this.normalizedUsers.filter(emp => {
      if (!emp.channels?.instore) return false;
      const state = emp.channels.instore.state;
      return state === 'with_guest' || state === 'with_guest_delivery';
    }).sort((a, b) => (a.channels?.instore?.order ?? 0) - (b.channels?.instore?.order ?? 0));

    // Phone Ups: All employees with phone eligibility (excluding globally inactive)
    // Story 5.3: Use isEmployeeInactive() instead of per-channel check
    this.phoneUpsEmployees = this.normalizedUsers.filter(emp => 
      emp.channels?.phone?.eligible === true && !isEmployeeInactive(emp)
    ).sort((a, b) => (a.channels?.phone?.order ?? 0) - (b.channels?.phone?.order ?? 0));

    // Internet Ups: All employees with online eligibility (excluding globally inactive)
    // Story 5.3: Use isEmployeeInactive() instead of per-channel check
    this.internetUpsEmployees = this.normalizedUsers.filter(emp => 
      emp.channels?.online?.eligible === true && !isEmployeeInactive(emp)
    ).sort((a, b) => (a.channels?.online?.order ?? 0) - (b.channels?.online?.order ?? 0));
  }

  /**
   * Determine if an employee is unavailable in the given channel.
   * Used for visual indication in phone/online columns.
   * 
   * Includes cross-channel UP/On Deck unavailability logic:
   * - If employee is UP for instore, they are unavailable in phone/online
   *   unless the board config explicitly allows it (allowUpToTakePhone/allowUpToTakeInternet)
   * - If employee is On Deck for instore, they may be unavailable based on config
   *   (allowOnDeckToTakePhone/allowOnDeckToTakeInternet - default true)
   * 
   * @param emp Normalized employee
   * @param channel Target channel
   * @returns true if employee is unavailable in this channel
   */
  isEmployeeUnavailable(emp: NormalizedEmployee, channel: ChannelType): boolean {
    // First check base unavailability (break, busy, inactive, not eligible)
    if (isEmployeeUnavailable(emp, channel)) {
      return true;
    }
    
    // Cross-channel UP/On Deck unavailability only applies to phone/online channels
    if (channel === 'instore') {
      return false;
    }
    
    // Cross-channel UP/On Deck rules only apply when instore channel is BOTH purchased AND enabled
    // When instore is disabled or not purchased, there is no instore queue to be "UP" or "On Deck" for
    const instorePurchased = this.boardConfig?.instoreChannelPurchased !== false;
    const instoreEnabled = this.boardConfig?.enableInstoreChannel !== false;
    if (!instorePurchased || !instoreEnabled) {
      return false;
    }
    
    // FR-P3-003, FR-P3-004, FR-P3-005: Cross-channel UP/On Deck unavailability
    const isUpForInstore = this.instoreUp?.email === emp.email;
    const isOnDeckForInstore = this.instoreOnDeck?.email === emp.email;
    
    if (channel === 'phone') {
      // Check if UP for instore should be unavailable for phone
      // Default: allowUpToTakePhone = false (UP is unavailable)
      if (isUpForInstore && !this.boardConfig?.allowUpToTakePhone) {
        return true;
      }
      // Check if On Deck for instore should be unavailable for phone
      // Default: allowOnDeckToTakePhone = true (On Deck is available)
      if (isOnDeckForInstore && this.boardConfig?.allowOnDeckToTakePhone === false) {
        return true;
      }
    }
    
    if (channel === 'online') {
      // Check if UP for instore should be unavailable for online
      // Default: allowUpToTakeInternet = false (UP is unavailable)
      if (isUpForInstore && !this.boardConfig?.allowUpToTakeInternet) {
        return true;
      }
      // Check if On Deck for instore should be unavailable for online
      // Default: allowOnDeckToTakeInternet = true (On Deck is available)
      if (isOnDeckForInstore && this.boardConfig?.allowOnDeckToTakeInternet === false) {
        return true;
      }
    }
    
    return false;
  }

  /**
   * Convert a NormalizedEmployee to the legacy Employee format for display components.
   * This is needed because the employee display component expects the legacy format.
   * 
   * @param normalized The normalized employee
   * @returns Employee in legacy format
   */
  normalizedToEmployee(normalized: NormalizedEmployee): Employee {
    // Convert from normalized format back to legacy format for compatibility
    // The legacy Employee type has state/order at root level
    const legacyState = normalized.globalState === 'break'
      ? 'break'
      : normalized.channels.instore.state;
    
    return {
      email: normalized.email,
      name: normalized.name,
      phone: normalized.phone,
      photoUrl: normalized.photoUrl,
      roles: normalized.roles,
      smsNotify: normalized.smsNotify,
      state: legacyState,
      order: normalized.channels.instore.order,
      authToken: undefined,
      globalState: normalized.globalState,
      channels: normalized.channels,
    } as Employee;
  }

  ngOnInit(): void {
    $('#view-container').on('fullscreenchange', this.onFullScreenChange);

    this.auth.user.pipe(takeUntil(this.destroy$)).subscribe((u) => {
      if (!u || !u.email) return;

      this.boardId = this.route.snapshot.paramMap.get('id');
      this.loaded = true;

      if (!this.boardId) return;

      // Subscribe to current user's board access
      this.db2
        .collection('boards')
        .doc(this.boardId)
        .collection('users')
        .doc<BoardUserData>(u.email)
        .valueChanges()
        .pipe(takeUntil(this.destroy$))
        .subscribe((user) => {
          this.boardUser = user;
          this.cdr.markForCheck();
        });

      // Subscribe to board configuration (for enabled channels)
      this.db2
        .collection('boards')
        .doc<any>(this.boardId)
        .valueChanges()
        .pipe(takeUntil(this.destroy$))
        .subscribe((d) => {
          this.board = d?.data;
          this.boardConfig = d?.config || {};
          
          // DEBUG: Log config changes for Story 3.9 debugging
          console.log('[DEBUG Story 3.9 VIEWER] Board config loaded:', JSON.stringify(this.boardConfig));
          console.log('[DEBUG Story 3.9 VIEWER] useModernLayout value:', this.boardConfig?.useModernLayout, 'type:', typeof this.boardConfig?.useModernLayout);
          
          // Update enabled channels based on config
          this.updateEnabledChannels(this.boardConfig);
          
          // Access config with safe navigation and default
          // Deliveries are an instore feature - only show if instore channel is purchased AND enabled AND showDelivery is enabled
          const instorePurchased = d?.config?.instoreChannelPurchased !== false;
          const instoreEnabled = d?.config?.enableInstoreChannel !== false;
          const deliveryEnabled = d?.config?.showDelivery !== false;
          this.showDelivery = instorePurchased && instoreEnabled && deliveryEnabled;
          if (!this.board) {
            this.cdr.markForCheck();
            return;
          }

          this.board.viewerBackground =
            this.board.viewerBackground || 'assets/light-wood.jpg';
          $('#view-container').css(
            'background-image',
            `url(${this.board.viewerBackground})`
          );

          this.deliveries = [];
          if (this.board?.deliveries) {
            for (const delivery of this.board.deliveries) {
              this.deliveries.push({
                id: delivery.id,
                customer_in_store: delivery.customer_in_store,
                arrival_time: new Date(delivery.arrival_time),
                stock_number: delivery.stock_number,
                vehicle: delivery.vehicle,
                emp: delivery.emp,
              });
            }
          }
          
          // Recalculate UP/On Deck when config changes (affects isEmployeeUnavailable)
          // This ensures green/yellow indicators update immediately when admin toggles config
          this.calculateUpAndOnDeck();
          
          this.cdr.markForCheck();
        });

      // Subscribe to employees collection
      this.db2
        .collection('boards')
        .doc(this.boardId)
        .collection<BoardUserData>('users')
        .valueChanges({ idField: 'email' })
        .pipe(takeUntil(this.destroy$))
        .subscribe((usrs) => {
          this.users = usrs;

          // Normalize employees to multi-channel format
          // Cast BoardUserData to RawEmployee for normalization (compatible structure)
          this.normalizedUsers = normalizeEmployees(usrs as any);
          
          // Compute per-channel employee lists
          this.updateEmployeeLists();

          // Calculate active employees (non-inactive) for legacy display
          this.active = getUsersNotInState(this.users, 'inactive');

          // Calculate up and on deck
          let available = [];
          for (let i = 0; i < this.active.length; i++) {
            let emp = this.active[i];
            if (emp.state == 'up') {
              available.push(emp);
            }
          }

          this.up = available[0];
          this.onDeck = available[1];
          
          // Calculate per-channel UP/On Deck using the extracted method
          this.calculateUpAndOnDeck();
          
          this.cdr.markForCheck();
        });
    });
  }

}

export class EmployeeWithIndex {
  emp: Employee;
  index: number;
  constructor(emp: Employee, index: number) {
    this.emp = emp;
    this.index = index;
  }
}
